import { useCallback, useEffect, useRef, useState } from "react";
import { DISCORD_IMAGE_PRESETS } from "../../shared/config/discord-image-presets";
import { AdminUsageSettings } from "./AdminUsageSettings";
import { AdminContextPolicySettings } from "./AdminContextPolicySettings";
import { UserCharacterSettings } from "./UserCharacterSettings";
import { AdminPromptSettings } from "./AdminPromptSettings";
import { AdminSearchTermsSettings } from "./AdminSearchTermsSettings";
import { reconcileCharacterMetadata } from "./character-metadata";
import { settingsApi, settingsSaveErrorMessage } from "./api";
import { InteractionRecords } from "./InteractionRecords";
import { ImageJobQueueSettings } from "./ImageJobQueueSettings";
import { ImagePresetsSettings } from "./ImagePresetsSettings";
import { ImageUsageMeter } from "./ImageUsageMeter";
import { ManualImageSettings } from "./ManualImageSettings";
import { CharacterSettings } from "./PersonaSettings";
import { Skeleton, SkeletonGrid, SkeletonImage } from "./Skeleton";
import { AdminRolesSettings, TitleDeletionSettings, TitleManagementSettings } from "./TitleSettings";
import "./image-filter.css";
import { findImageModel, mergeImageModels, normalizeImageModelRef, type ImageModel } from "./image-models";
import { appendBlankMemoryDraft, type MemoryDraft } from "./memory-drafts";
import { settingsValidationError, toEditableSettings, type EditableSettings as EditableSettingsShape } from "./settings-form";
import { isSectionDirty, mergeSectionSnapshotIntoValues, sectionDraftSignature, SettingsSaveController, type SettingsDraftState, type SettingsSaveSection } from "./settings-save-controller";
import type { AccessSnapshot, MemoryRecord, SettingsSection, SettingsSnapshot } from "./types";

interface NavigationSection { id: SettingsSection; label: string; description: string }
type NavigationGroupId = "character" | "image" | "title" | "admin";
interface NavigationGroup { id: NavigationGroupId; label: string; sections: NavigationSection[] }

const MY_CHARACTERS_SECTION = { id: "characters" as const, label: "我的角色", description: "创建并全局维护你拥有的角色；修改会对所有使用者生效。" };
const PERSONA_SECTION = { id: "persona" as const, label: "自定角色", description: "选择当前角色，并维护只对你生效的角色设置。" };
const SYSTEM_PROMPT_SECTION = { id: "system_prompt" as const, label: "系统题词", description: "控制只对你的助手生效的基础行为。" };
const MEMORY_SECTION = { id: "memory" as const, label: "我的记忆", description: "只保留真正长期有用的信息；普通记忆会按服务端规则过期。" };
const STYLE_SECTION = { id: "style" as const, label: "文风题词", description: "决定回复的表达方式。" };
const IMAGE_JOBS_SECTION = { id: "image_jobs" as const, label: "任务队列", description: "查看最近 7 天内的生图任务及处理结果。" };
const IMAGE_PRESETS_SECTION = { id: "image_presets" as const, label: "预设列表", description: "保存常用的正负提示词，可填入新建任务或直接生成。" };
const MODEL_SECTION = { id: "model" as const, label: "模型选择", description: "选择图片生成使用的 Checkpoint 模型。" };
const MANUAL_IMAGE_SECTION = { id: "manual_image" as const, label: "新建任务", description: "配置图片参数、填写题词并创建新的生图任务。" };
const TITLE_SECTION = { id: "title" as const, label: "头衔管理", description: "编辑显示在你的 Discord 身份旁的个人头衔。" };
const TITLE_DELETE_SECTION = { id: "title_delete" as const, label: "删除头衔", description: "彻底删除个人头衔；每个北京时间自然日只能操作一次。" };
const ADMIN_TITLES_SECTION = { id: "admin_titles" as const, label: "头衔配置", description: "选择哪些服务器角色可作为自定义头衔使用。" };
const ADMIN_USAGE_SECTION = { id: "admin_usage" as const, label: "用量统计", description: "查看 LLM 与图片服务开销，并管理图片每日额度。" };
const ADMIN_CHARACTERS_SECTION = { id: "admin_characters" as const, label: "角色管理", description: "管理全部角色资料、作者、共享声源与化身绑定。" };
const ADMIN_CONTEXT_POLICY_SECTION = { id: "admin_context_policy" as const, label: "上下文设置", description: "设置沿 Discord 回复链追溯的最高消息数量。" };
const ADMIN_PROMPTS_SECTION = { id: "admin_prompts" as const, label: "系统题词", description: "查看并编辑机器人使用的系统题词注册表。" };
const SEARCH_TERMS_SECTION = { id: "admin_search_terms" as const, label: "术语列表", description: "管理搜索时需要识别的术语、别名和说明。" };
const INTERACTIONS_SECTION = { id: "interactions" as const, label: "交互记录", description: "查看你创建且未绑定化身的角色，或你所拥有化身在最近 7 天内的匿名交互。" };

interface EditableMemory extends MemoryRecord {
  deleted: boolean;
  originalMemory: string;
  originalPermanent: boolean;
}

function emptySnapshot(): SettingsSnapshot {
  return {
    uid: "",
    memories: [],
    system_prompt: "",
    style_guide: "",
    image_model: "",
    image_cfg: "5",
    image_sampler: "Euler a",
    image_scheduler: "normal",
    image_resolution: "720x1280",
    selected_persona: "",
    unlocked_personas: [],
    character_usage_counts: {},
    context: { avatar: null },
    persona_available: false,
    title: "",
    title_enabled: false,
    revision: 0
  };
}

function normalizeSnapshot(snapshot: SettingsSnapshot): SettingsSnapshot {
  const defaults = emptySnapshot();
  return {
    ...snapshot,
    image_cfg: snapshot.image_cfg || defaults.image_cfg,
    image_sampler: snapshot.image_sampler || defaults.image_sampler,
    image_scheduler: snapshot.image_scheduler || defaults.image_scheduler,
    image_resolution: snapshot.image_resolution || defaults.image_resolution,
    image_model: normalizeImageModelRef(snapshot.image_model),
  };
}

function dateLabel(value: string): string {
  return value.slice(0, 10);
}

function PanelError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div className="panel-error" role="alert">
    <p>{message}</p>
    <button className="secondary outlined" type="button" onClick={onRetry}>重试</button>
  </div>;
}

function SettingsPanelSkeleton({ variant, label }: { variant: "form" | "list"; label: string }) {
  if (variant === "list") return <SkeletonGrid items={4} variant="row" label={label} />;
  return <div className="form-grid" role="status" aria-label={label}>
    <span className="sr-only">{label}</span>
    {Array.from({ length: 6 }, (_, index) => <label className="skeleton-field" aria-hidden="true" key={index}>
      <Skeleton width={index % 2 === 0 ? "42%" : "58%"} />
      <Skeleton variant="control" />
    </label>)}
  </div>;
}

export function App() {
  const [section, setSection] = useState<SettingsSection>("memory");
  const [mountedSections, setMountedSections] = useState<Set<SettingsSection>>(() => new Set(["memory"]));
  const [openNavigationGroup, setOpenNavigationGroup] = useState<NavigationGroupId | null>("character");
  const [personaQuery, setPersonaQuery] = useState("");
  const [imagePromptTransfer, setImagePromptTransfer] = useState<{ id: string; positive_prompt: string; negative_prompt: string }>();
  const [imageUsageRevision, setImageUsageRevision] = useState(0);
  const [imagePresetRevision, setImagePresetRevision] = useState(0);
  const [searchTermsHeaderTarget, setSearchTermsHeaderTarget] = useState<HTMLDivElement | null>(null);
  const searchTermsHeaderRef = useCallback((node: HTMLDivElement | null) => setSearchTermsHeaderTarget(node), []);
  const [imageQueueHeaderTarget, setImageQueueHeaderTarget] = useState<HTMLDivElement | null>(null);
  const imageQueueHeaderRef = useCallback((node: HTMLDivElement | null) => setImageQueueHeaderTarget(node), []);
  const [saved, setSaved] = useState<SettingsSnapshot>(emptySnapshot);
  const [memories, setMemories] = useState<EditableMemory[]>([]);
  const [drafts, setDrafts] = useState<MemoryDraft[]>([]);
  const [values, setValues] = useState<EditableSettingsShape>(() => toEditableSettings(emptySnapshot()));
  const [status, setStatus] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsLoadError, setSettingsLoadError] = useState("");
  const [savingSections, setSavingSections] = useState<Set<SettingsSaveSection>>(() => new Set());
  const [failedAutoSections, setFailedAutoSections] = useState<Set<SettingsSaveSection>>(() => new Set());
  const [saveStatuses, setSaveStatuses] = useState<Map<SettingsSaveSection, string>>(() => new Map());
  const [access, setAccess] = useState<AccessSnapshot | null>(null);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const [accessError, setAccessError] = useState("");
  const avatarRefreshGeneration = useRef(0);
  const accessRef = useRef(access);
  const savedRef = useRef(saved);
  const valuesRef = useRef(values);
  const memoriesRef = useRef(memories);
  const draftsRef = useRef(drafts);
  const savingCounts = useRef(new Map<SettingsSaveSection, number>());
  const autoSaveTimers = useRef(new Map<SettingsSaveSection, ReturnType<typeof setTimeout>>());
  const failedRequests = useRef(new Map<SettingsSaveSection, { requestId: string; signature: string }>());
  const saveController = useRef<SettingsSaveController | null>(null);
  if (saveController.current === null) saveController.current = new SettingsSaveController(saved, (changes) => {
    if (accessRef.current?.title_deletion.can_edit !== false) return settingsApi.save(changes);
    return settingsApi.save({
      title_enabled: changes.title_enabled,
      request_id: changes.request_id,
      expected_revision: changes.expected_revision,
    });
  });
  accessRef.current = access;
  savedRef.current = saved;
  valuesRef.current = values;
  memoriesRef.current = memories;
  draftsRef.current = drafts;

  function hydrate(snapshot: SettingsSnapshot): void {
    const normalized = normalizeSnapshot(snapshot);
    saveController.current?.replaceSnapshot(normalized);
    savedRef.current = normalized;
    setSaved(normalized);
    const nextMemories = normalized.memories.map((memory) => ({ ...memory, deleted: false, originalMemory: memory.memory, originalPermanent: memory.permanent }));
    memoriesRef.current = nextMemories;
    setMemories(nextMemories);
    draftsRef.current = [];
    setDrafts([]);
    const nextValues = toEditableSettings(normalized);
    valuesRef.current = nextValues;
    setValues(nextValues);
  }

  function loadSettings(): void {
    setSettingsLoading(true);
    setSettingsLoadError("");
    void settingsApi.load().then((snapshot) => {
      hydrate(snapshot);
      setStatus("");
    }).catch(() => setSettingsLoadError("无法读取设置，请确认正从 Discord Activity 打开。"))
      .finally(() => setSettingsLoading(false));
  }

  function loadAccess(): void {
    setAccessLoaded(false);
    setAccessError("");
    void settingsApi.loadAccess().then(setAccess).catch(() => {
      setAccess(null);
      setAccessError("无法读取访问权限，请稍后重试。");
    }).finally(() => setAccessLoaded(true));
  }

  useEffect(() => {
    loadSettings();
    loadAccess();
    return () => {
      for (const timer of autoSaveTimers.current.values()) clearTimeout(timer);
      autoSaveTimers.current.clear();
    };
  }, []);

  const draftState: SettingsDraftState = { values, memories, drafts };
  const imageSettingsDirty = saved.image_model !== values.image_model
    || saved.image_cfg !== values.image_cfg
    || saved.image_sampler !== values.image_sampler
    || saved.image_scheduler !== values.image_scheduler
    || saved.image_resolution !== values.image_resolution
    || savingSections.has("image")
    || savingSections.has("model");
  const validationError = access?.title_deletion.can_edit === false ? "" : settingsValidationError(values);

  function updateValues(patch: Partial<EditableSettingsShape>): EditableSettingsShape {
    const next = { ...valuesRef.current, ...patch };
    valuesRef.current = next;
    setValues(next);
    return next;
  }

  function updateMemories(updater: (current: EditableMemory[]) => EditableMemory[]): void {
    const next = updater(memoriesRef.current);
    memoriesRef.current = next;
    setMemories(next);
  }

  function updateDrafts(updater: (current: MemoryDraft[]) => MemoryDraft[]): void {
    const next = updater(draftsRef.current);
    draftsRef.current = next;
    setDrafts(next);
  }

  function updateSaved(updater: (current: SettingsSnapshot) => SettingsSnapshot): void {
    const next = updater(savedRef.current);
    savedRef.current = next;
    saveController.current?.replaceSnapshot(next);
    setSaved(next);
  }

  function setSectionSaving(section: SettingsSaveSection, saving: boolean): void {
    const count = Math.max(0, (savingCounts.current.get(section) ?? 0) + (saving ? 1 : -1));
    if (count === 0) savingCounts.current.delete(section);
    else savingCounts.current.set(section, count);
    setSavingSections(new Set(savingCounts.current.keys()));
  }

  function currentDraft(valuesOverride = valuesRef.current): SettingsDraftState {
    return { values: valuesOverride, memories: memoriesRef.current, drafts: draftsRef.current };
  }

  function retryRequestId(section: SettingsSaveSection, submitted: SettingsDraftState): string {
    const failed = failedRequests.current.get(section);
    if (failed?.signature === sectionDraftSignature(section, submitted)) return failed.requestId;
    return crypto.randomUUID();
  }

  function setSaveStatus(section: SettingsSaveSection, message: string): void {
    setSaveStatuses((current) => {
      const next = new Map(current);
      if (message) next.set(section, message);
      else next.delete(section);
      return next;
    });
  }

  async function saveSectionDraft(section: SettingsSaveSection, submitted: SettingsDraftState, automatic: boolean): Promise<void> {
    if (section === "title") {
      const error = access?.title_deletion.can_edit === false ? "" : settingsValidationError(submitted.values);
      if (error) { setSaveStatus(section, error); return; }
    }
    setSectionSaving(section, true);
    const requestId = retryRequestId(section, submitted);
    const controller = saveController.current;
    if (controller === null) {
      setSectionSaving(section, false);
      return;
    }
    try {
      const result = await controller.save(section, submitted, requestId);
      const snapshot = normalizeSnapshot(result.snapshot);
      savedRef.current = snapshot;
      setSaved(snapshot);
      if (section === "memory") {
        if (memoriesRef.current === submitted.memories && draftsRef.current === submitted.drafts) {
          const nextMemories = snapshot.memories.map((memory) => ({ ...memory, deleted: false, originalMemory: memory.memory, originalPermanent: memory.permanent }));
          memoriesRef.current = nextMemories;
          draftsRef.current = [];
          setMemories(nextMemories);
          setDrafts([]);
        }
      } else {
        const nextValues = mergeSectionSnapshotIntoValues(valuesRef.current, submitted.values, snapshot, section);
        valuesRef.current = nextValues;
        setValues(nextValues);
      }
      if (!result.skipped) setSaveStatus(section, automatic ? "已自动保存" : "已保存");
      failedRequests.current.delete(section);
      setFailedAutoSections((current) => {
        const next = new Set(current);
        next.delete(section);
        return next;
      });
    } catch (error) {
      failedRequests.current.set(section, { requestId, signature: sectionDraftSignature(section, submitted) });
      const message = settingsSaveErrorMessage(error);
      setSaveStatus(section, `${message} 草稿已保留${automatic ? "，可点击重试。" : "，请再次保存重试。"}`);
      if (automatic) setFailedAutoSections((current) => new Set(current).add(section));
    } finally {
      setSectionSaving(section, false);
    }
  }

  function queueAutoSave(section: "persona" | "image" | "model" | "title", nextValues: EditableSettingsShape, delay = 0): void {
    const existing = autoSaveTimers.current.get(section);
    if (existing !== undefined) clearTimeout(existing);
    const submit = () => {
      autoSaveTimers.current.delete(section);
      void saveSectionDraft(section, currentDraft(nextValues), true);
    };
    if (delay === 0) submit();
    else autoSaveTimers.current.set(section, setTimeout(submit, delay));
  }

  function updateAutoSavedValues(section: "persona" | "image" | "model" | "title", patch: Partial<EditableSettingsShape>, delay = 0): void {
    const next = updateValues(patch);
    if (section === "title" && access?.title_deletion.can_edit !== false && settingsValidationError(next)) {
      const timer = autoSaveTimers.current.get(section);
      if (timer !== undefined) clearTimeout(timer);
      autoSaveTimers.current.delete(section);
      setSaveStatus(section, settingsValidationError(next));
      return;
    }
    if (section === "image" && !next.image_cfg.trim()) {
      const timer = autoSaveTimers.current.get(section);
      if (timer !== undefined) clearTimeout(timer);
      autoSaveTimers.current.delete(section);
      setSaveStatus(section, "CFG 不能为空，草稿尚未保存。");
      return;
    }
    queueAutoSave(section, next, delay);
  }

  function setDraft(index: number, patch: Partial<{ content: string; permanent: boolean }>): void {
    updateDrafts((current) => {
      const next = current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item);
      return next;
    });
  }

  async function refreshAvatarMetadata(): Promise<void> {
    const generation = ++avatarRefreshGeneration.current;
    try {
      const [snapshot, nextAccess] = await Promise.all([settingsApi.load(), settingsApi.loadAccess()]);
      if (generation !== avatarRefreshGeneration.current) return;
      // 角色 mutation 只刷新角色元数据。先把失效的角色选择回落到仍解锁的服务端选择，
      // 再替换 unlocked 列表；即使 state 更新没有被批处理，中间 render 也保持合法。
      const reconciled = reconcileCharacterMetadata(savedRef.current, valuesRef.current, snapshot);
      valuesRef.current = reconciled.values;
      setValues(reconciled.values);
      savedRef.current = reconciled.saved;
      saveController.current?.replaceSnapshot(reconciled.saved);
      setSaved(reconciled.saved);
      setAccess(nextAccess);
      setStatus("角色元数据已更新");
    } catch {
      if (generation !== avatarRefreshGeneration.current) return;
      setStatus("角色操作已完成，但角色元数据暂时无法刷新。重新打开面板后会同步。");
    }
  }

  const navigationGroups: NavigationGroup[] = [
    { id: "character", label: "角色", sections: [MY_CHARACTERS_SECTION, PERSONA_SECTION, INTERACTIONS_SECTION, SYSTEM_PROMPT_SECTION, STYLE_SECTION, MEMORY_SECTION, SEARCH_TERMS_SECTION] },
    { id: "image", label: "图片", sections: [IMAGE_JOBS_SECTION, IMAGE_PRESETS_SECTION, MODEL_SECTION, MANUAL_IMAGE_SECTION] },
    { id: "title", label: "头衔", sections: [TITLE_SECTION, TITLE_DELETE_SECTION] },
    ...(access?.is_admin ? [{ id: "admin" as const, label: "管理员", sections: [ADMIN_CHARACTERS_SECTION, ADMIN_TITLES_SECTION, ADMIN_PROMPTS_SECTION, ADMIN_CONTEXT_POLICY_SECTION, ADMIN_USAGE_SECTION] }] : []),
  ];
  const sections = navigationGroups.flatMap((group) => group.sections);
  const active = sections.find((item) => item.id === section) ?? sections[0];
  const activeSection = active.id;
  const activeDescription = active.description;

  function openSection(nextSection: SettingsSection): void {
    setMountedSections((current) => current.has(nextSection) ? current : new Set(current).add(nextSection));
    const nextGroup = navigationGroups.find((group) => group.sections.some((item) => item.id === nextSection));
    if (nextGroup) setOpenNavigationGroup(nextGroup.id);
    setSection(nextSection);
  }

  function fillImageTask(prompts: { positive_prompt: string; negative_prompt: string }): void {
    setImagePromptTransfer({ ...prompts, id: crypto.randomUUID() });
    openSection("manual_image");
  }

  function characterManager(mode: "mine" | "admin") {
    return <UserCharacterSettings isAdmin={mode === "admin"} actorUid={saved.uid} mode={mode} onBindingChanged={() => { void refreshAvatarMetadata(); }} onProfileSaved={(profile) => updateSaved((current) => ({ ...current, unlocked_personas: current.unlocked_personas.map((persona) => persona.name === profile.id ? { ...persona, character: { ...persona.character, profile_text: profile.content, profile_revision: profile.profile_revision } } : persona) }))} onSdPromptsSaved={(prompts) => updateSaved((current) => ({ ...current, unlocked_personas: current.unlocked_personas.map((persona) => persona.name === prompts.id ? { ...persona, character: { ...persona.character, sd_positive_prompt: prompts.positive_prompt, sd_negative_prompt: prompts.negative_prompt, sd_revision: prompts.sd_revision } } : persona) }))} onImageSaved={(image) => updateSaved((current) => ({ ...current, unlocked_personas: current.unlocked_personas.map((persona) => persona.name !== image.id ? persona : { ...persona, character: { ...persona.character, ...(image.kind === "portrait" ? { portrait_url: image.url, portrait_revision: image.revision } : { cover_url: image.url, cover_revision: image.revision }) } }) }))} />;
  }

  function renderSection(activeSection: SettingsSection) {
    const savingActiveSection = savingSections.has(activeSection as SettingsSaveSection);
    const retryAutoSave = failedAutoSections.has(activeSection as SettingsSaveSection)
      ? <div className="section-save-actions"><button className="primary" type="button" disabled={savingActiveSection} onClick={() => void saveSectionDraft(activeSection as SettingsSaveSection, currentDraft(), true)}>{savingActiveSection ? "保存中…" : "重试自动保存"}</button></div>
      : null;

    if (activeSection === "admin_usage") return <AdminUsageSettings />;
    if (activeSection === "admin_prompts") return <AdminPromptSettings />;
    if (activeSection === "admin_search_terms") return <AdminSearchTermsSettings headerActionsTarget={searchTermsHeaderTarget} />;
    if (activeSection === "admin_context_policy") return <AdminContextPolicySettings />;
    if (activeSection === "admin_titles") return <AdminRolesSettings />;
    if (activeSection === "characters") return characterManager("mine");
    if (activeSection === "admin_characters") {
      if (!accessLoaded) return <SettingsPanelSkeleton variant="list" label="正在读取访问权限" />;
      if (access === null) return <PanelError message={accessError || "无法读取访问权限，请稍后重试。"} onRetry={loadAccess} />;
      if (!access.is_admin) return <p className="panel-state">只有管理员可以管理全部角色。</p>;
      return characterManager("admin");
    }
    if (activeSection === "interactions") {
      if (!accessLoaded) return <SettingsPanelSkeleton variant="list" label="正在读取交互记录权限" />;
      return access?.has_interactions ? <InteractionRecords /> : <p className="panel-state">你目前没有可查看的交互记录。创建角色或成为化身 owner 后，这里会显示最近 7 天的匿名交互。</p>;
    }
    if (activeSection === "persona") {
      if (settingsLoadError) return <PanelError message={settingsLoadError} onRetry={loadSettings} />;
      return <><CharacterSettings query={personaQuery} onImagePromptTransfer={fillImageTask} loading={settingsLoading} values={values} unlockedPersonas={saved.unlocked_personas} characterUsageCounts={saved.character_usage_counts} hasLockedPersonas={saved.has_locked_personas ?? true} available={saved.persona_available} onChange={(patch) => updateAutoSavedValues("persona", patch)} onRetry={loadSettings} onInteractionConsented={(characterId) => updateSaved((current) => { const selected = current.unlocked_personas.find((persona) => persona.name === characterId)?.interaction; return { ...current, context: selected ? { ...current.context, interaction: { character_id: characterId, ...selected, consented: true } } : current.context, unlocked_personas: current.unlocked_personas.map((persona) => persona.name === characterId && persona.interaction ? { ...persona, interaction: { ...persona.interaction, consented: true } } : persona) }; })} onAvatarConsented={(characterId, bindingVersion) => updateSaved((current) => ({ ...current, context: { ...current.context, avatar: { character_id: characterId, binding_version: bindingVersion, consented: true } }, unlocked_personas: current.unlocked_personas.map((persona) => persona.name === characterId && persona.avatar?.binding_version === bindingVersion ? { ...persona, avatar: { ...persona.avatar, consented: true } } : persona) }))} onImagePromptsSaved={(prompts) => updateSaved((current) => ({ ...current, unlocked_personas: current.unlocked_personas.map((persona) => persona.name === prompts.id ? { ...persona, character: { ...persona.character, sd_positive_prompt: prompts.effective_positive_prompt, sd_negative_prompt: prompts.effective_negative_prompt } } : persona) }))} />{retryAutoSave}</>;
    }
    if (activeSection === "memory") {
      return settingsLoading ? <SettingsPanelSkeleton variant="list" label="正在读取记忆" /> : settingsLoadError ? <PanelError message={settingsLoadError} onRetry={loadSettings} /> : <MemoryEditor memories={memories} drafts={drafts} disabled={savingActiveSection} onMemoryChange={(id, patch) => updateMemories((current) => current.map((memory) => memory.id === id ? { ...memory, ...patch } : memory))} onToggleDelete={(id) => updateMemories((current) => current.map((memory) => memory.id === id ? { ...memory, deleted: !memory.deleted } : memory))} onDraftChange={setDraft} onRemoveDraft={(index) => updateDrafts((current) => current.filter((_, itemIndex) => itemIndex !== index))} />;
    }
    if (activeSection === "image_jobs") return <ImageJobQueueSettings active={section === "image_jobs"} headerActionsTarget={imageQueueHeaderTarget} onFill={fillImageTask} />;
    if (activeSection === "image_presets") return <ImagePresetsSettings active={section === "image_presets"} refreshKey={imagePresetRevision} onFill={fillImageTask} onJobQueued={() => setImageUsageRevision((value) => value + 1)} />;
    if (activeSection === "model") {
      if (settingsLoading) return <SkeletonGrid items={6} variant="persona" label="正在读取模型设置" />;
      if (settingsLoadError) return <PanelError message={settingsLoadError} onRetry={loadSettings} />;
      return <><ModelSettings values={values} onChange={(patch) => updateAutoSavedValues("model", patch)} />{retryAutoSave}</>;
    }
    if (activeSection === "manual_image") {
      if (settingsLoading) return <SettingsPanelSkeleton variant="form" label="正在读取手动生图设置" />;
      if (settingsLoadError) return <PanelError message={settingsLoadError} onRetry={loadSettings} />;
      return <ManualImageSettings settingsDirty={imageSettingsDirty} active={section === "manual_image"} promptTransfer={imagePromptTransfer} onPresetSaved={() => setImagePresetRevision((value) => value + 1)} onJobQueued={() => setImageUsageRevision((value) => value + 1)} settingsPanel={<section className="image-parameter-settings" aria-labelledby="image-parameter-heading"><h3 id="image-parameter-heading">设置选项</h3><ImageSettings values={values} onChange={(patch) => updateAutoSavedValues("image", patch, 500)} />{retryAutoSave}</section>} />;
    }
    if (activeSection === "title") {
      if (!accessLoaded) return <SettingsPanelSkeleton variant="list" label="正在读取访问权限" />;
      if (access === null) return <PanelError message={accessError || "无法读取访问权限，请稍后重试。"} onRetry={loadAccess} />;
      if (settingsLoading) return <SettingsPanelSkeleton variant="form" label="正在读取头衔配置" />;
      if (settingsLoadError) return <PanelError message={settingsLoadError} onRetry={loadSettings} />;
      return <><TitleManagementSettings values={values} access={access.title_deletion} onChange={(patch) => updateAutoSavedValues("title", patch, 500)} />{retryAutoSave}</>;
    }
    if (activeSection === "title_delete") {
      if (!accessLoaded) return <SettingsPanelSkeleton variant="list" label="正在读取删除额度" />;
      if (access === null) return <PanelError message={accessError || "无法读取访问权限，请稍后重试。"} onRetry={loadAccess} />;
      return <TitleDeletionSettings access={access.title_deletion} deletionDisabled={savingSections.has("title") || isSectionDirty(saved, draftState, "title")} onDeleted={(snapshot) => { const timer = autoSaveTimers.current.get("title"); if (timer !== undefined) clearTimeout(timer); autoSaveTimers.current.delete("title"); failedRequests.current.delete("title"); setFailedAutoSections((current) => { const next = new Set(current); next.delete("title"); return next; }); const normalized = normalizeSnapshot(snapshot); const nextValues = mergeSectionSnapshotIntoValues(valuesRef.current, valuesRef.current, normalized, "title"); valuesRef.current = nextValues; setValues(nextValues); savedRef.current = normalized; saveController.current?.replaceSnapshot(normalized); setSaved(normalized); setAccess((current) => current ? { ...current, title_deletion: { ...current.title_deletion, can_delete: false, deleted_today: true } } : current); setSaveStatus("title", "头衔已彻底删除"); }} onAccessChange={setAccess} />;
    }
    if (activeSection === "image") return <ImageSettings values={values} onChange={(patch) => updateAutoSavedValues("image", patch, 500)} />;
    if (activeSection !== "system_prompt" && activeSection !== "style") return null;
    if (settingsLoading) return <SettingsPanelSkeleton variant="form" label="正在读取设置" />;
    if (settingsLoadError) return <PanelError message={settingsLoadError} onRetry={loadSettings} />;
    return <TextSetting section={activeSection} values={values} disabled={savingActiveSection} onChange={(value) => updateValues({ [activeSection === "system_prompt" ? "system_prompt" : "style_guide"]: value })} />;
  }

  const activeSaving = savingSections.has(activeSection as SettingsSaveSection);
  const headerActions = activeSection === "image_jobs" ? <div className="image-queue-header-target" ref={imageQueueHeaderRef} /> : activeSection === "manual_image" ? <ImageUsageMeter active refreshKey={imageUsageRevision} /> : activeSection === "memory" ? <div className="content-heading-actions">
    <button className="panel-fab" type="button" aria-label="新建记忆" title="新建记忆" disabled={activeSaving || settingsLoading || Boolean(settingsLoadError)} onClick={() => updateDrafts(appendBlankMemoryDraft)}>+</button>
    <button className="panel-fab" type="button" aria-label="保存记忆" title="保存记忆" disabled={activeSaving || settingsLoading || Boolean(settingsLoadError) || !isSectionDirty(saved, draftState, "memory")} onClick={() => void saveSectionDraft("memory", currentDraft(), false)}>✓</button>
  </div> : activeSection === "system_prompt" || activeSection === "style" ? <div className="content-heading-actions">
    <button className="panel-fab" type="button" aria-label={activeSection === "system_prompt" ? "保存系统提示词" : "保存文风引导"} title={activeSection === "system_prompt" ? "保存系统提示词" : "保存文风引导"} disabled={activeSaving || settingsLoading || Boolean(settingsLoadError) || !isSectionDirty(saved, draftState, activeSection)} onClick={() => void saveSectionDraft(activeSection, currentDraft(), false)}>✓</button>
  </div> : activeSection === "admin_search_terms" ? <div className="content-heading-actions admin-term-header-actions" ref={searchTermsHeaderRef} /> : null;

  return <main className="settings-shell">
    <header><h1>我的设置</h1><span className="status" aria-live="polite">{activeSection === "title" || activeSection === "title_delete" ? validationError || saveStatuses.get("title") || status : saveStatuses.get(activeSection as SettingsSaveSection) || status}</span></header>
    <div className="settings-body">
      <nav className="settings-navigation" aria-label="设置栏目">{navigationGroups.map((group) => {
        const expanded = openNavigationGroup === group.id;
        return <div className="navigation-group" key={group.id}>
          <button className="navigation-group-trigger" type="button" aria-expanded={expanded} aria-controls={`navigation-group-${group.id}`} onClick={() => setOpenNavigationGroup((current) => current === group.id ? null : group.id)}><span>{group.label}</span><span className="navigation-group-chevron" aria-hidden="true" /></button>
          <div className="navigation-group-items" id={`navigation-group-${group.id}`} hidden={!expanded}>{group.sections.map((item) => <button className={activeSection === item.id ? "selected" : ""} type="button" key={item.id} onClick={() => openSection(item.id)}>{item.label}</button>)}</div>
        </div>;
      })}</nav>
      <section className="content"><div className="content-heading"><div className="content-heading-row"><h2>{active.label}</h2>{activeSection === "persona" ? <input className="persona-search" type="search" maxLength={100} value={personaQuery} aria-label="搜索自定角色" placeholder="搜索角色…" onChange={(event) => setPersonaQuery(event.target.value)} /> : headerActions}</div><p>{activeDescription}</p></div>
        <div className="content-panels">{sections.filter((item) => item.id === activeSection || mountedSections.has(item.id)).map((item) => <div data-settings-panel={item.id} hidden={item.id !== activeSection} key={item.id}>{renderSection(item.id)}</div>)}</div>
      </section>
    </div>
  </main>;
}

function TrashIcon() {
  return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7h16M10 11v6m4-6v6M9 7l1-2h4l1 2m-9 0 1 13h10l1-13" /></svg>;
}

function MemoryEditor({ memories, drafts, disabled, onMemoryChange, onToggleDelete, onDraftChange, onRemoveDraft }: { memories: EditableMemory[]; drafts: MemoryDraft[]; disabled: boolean; onMemoryChange: (id: number, patch: Partial<Pick<EditableMemory, "memory" | "permanent">>) => void; onToggleDelete: (id: number) => void; onDraftChange: (index: number, patch: Partial<MemoryDraft>) => void; onRemoveDraft: (index: number) => void }) {
  return <div className="memory-list">
    <p className="memory-hint">永久记忆不会按普通规则过期。</p>
    {memories.length === 0 && drafts.length === 0 && <p className="memory-empty" role="status">还没有任何记忆。在频道里让机器人记住某件事之后会出现在这里；也可以点右下角的 + 手动添加。</p>}
    {memories.map((memory) => <article className={`memory-row ${memory.deleted ? "pending-delete" : ""}`} key={memory.id}>
      <label className="memory-permanent"><input type="checkbox" checked={memory.permanent} disabled={disabled || memory.deleted} onChange={(event) => onMemoryChange(memory.id, { permanent: event.target.checked })} /><span className="switch-track" aria-hidden="true" /><span>永久</span></label>
      <div className="memory-content"><input aria-label={`已保存记忆 ${memory.id}`} type="text" disabled={disabled || memory.deleted} value={memory.memory} onChange={(event) => onMemoryChange(memory.id, { memory: event.target.value })} /><small>{memory.deleted ? "此记忆将在保存时删除。" : dateLabel(memory.created_date)}</small></div>
      <button className="delete" type="button" disabled={disabled} aria-label={memory.deleted ? "撤销删除该记忆" : "删除该记忆"} title={memory.deleted ? "撤销删除" : "删除"} onClick={() => onToggleDelete(memory.id)}>{memory.deleted ? "撤销" : <TrashIcon />}</button>
    </article>)}
    {drafts.map((draft, index) => <article className="memory-row new-memory" key={index}>
      <label className="memory-permanent"><input type="checkbox" checked={draft.permanent} disabled={disabled} onChange={(event) => onDraftChange(index, { permanent: event.target.checked })} /><span className="switch-track" aria-hidden="true" /><span>永久</span></label>
      <input aria-label={`新记忆 ${index + 1}`} type="text" disabled={disabled} placeholder="输入一条新记忆…" value={draft.content} onChange={(event) => onDraftChange(index, { content: event.target.value })} />
      <button className="delete draft-delete" type="button" disabled={disabled} aria-label="移除新记忆" title="移除" onClick={() => onRemoveDraft(index)}><TrashIcon /></button>
    </article>)}
  </div>;
}

type EditableSettings = EditableSettingsShape;

function TextSetting({ section, values, disabled, onChange }: { section: "system_prompt" | "style"; values: EditableSettings; disabled: boolean; onChange: (value: string) => void }) {
  const key = section === "system_prompt" ? "system_prompt" : "style_guide";
  return <div className="text-setting-panel"><label className="text-setting">内容<textarea disabled={disabled} value={values[key]} onChange={(event) => onChange(event.target.value)} placeholder={section === "system_prompt" ? "输入系统提示词…" : "输入希望助手遵循的文风…"} /></label></div>;
}

function ModelSettings({ values, onChange }: { values: EditableSettings; onChange: (patch: Partial<EditableSettings>) => void }) {
  const [models, setModels] = useState<ImageModel[]>(() => mergeImageModels([]));
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [modelTag, setModelTag] = useState("");
  const [committedFilters, setCommittedFilters] = useState({ query: "", tag: "" });
  const [versionModel, setVersionModel] = useState<ImageModel | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [requestGate] = useState(() => ({ generation: 0, loading: false }));
  const configuredRef = normalizeImageModelRef(values.image_model);
  const [selectedModel, setSelectedModel] = useState<ImageModel>(() => {
    const defaultModel = mergeImageModels([])[0];
    return defaultModel?.ref === configuredRef ? defaultModel : { ref: configuredRef, name: configuredRef };
  });

  function selectionFromCatalog(catalog: ImageModel[], ref: string): ImageModel | null {
    const contains = catalog.some((model) => model.ref === ref || model.versions?.some((version) => version.ref === ref));
    return contains ? findImageModel(catalog, ref) : null;
  }

  const selected = selectionFromCatalog(models, configuredRef)
    ?? (selectedModel.ref === configuredRef ? selectedModel : { ref: configuredRef, name: configuredRef });

  function filtersHaveTerms(filters: { query: string; tag: string }): boolean {
    return filters.query.length > 0 || filters.tag.length > 0;
  }

  function loadModels(
    cursor: string | undefined,
    filters: { query: string; tag: string },
    append: boolean,
    replaceRequest: boolean,
  ): void {
    if (!replaceRequest && requestGate.loading) return;
    const generation = replaceRequest ? ++requestGate.generation : requestGate.generation;
    requestGate.loading = true;
    setModelsLoading(true);
    setModelsError("");
    void settingsApi.loadImageModels(filters.query, cursor, filters.tag).then((catalog) => {
      if (generation !== requestGate.generation) return;
      const selectedFromPage = selectionFromCatalog(catalog.items, configuredRef);
      if (selectedFromPage) setSelectedModel(selectedFromPage);
      setModels((current) => {
        return mergeImageModels(
          append ? [...current, ...catalog.items] : catalog.items,
          !filtersHaveTerms(filters),
        );
      });
      setNextCursor(catalog.nextCursor ?? null);
    }).catch(() => {
      if (generation !== requestGate.generation) return;
      setModelsError("无法读取模型目录，请稍后重试。");
      if (!append) {
        setModels(mergeImageModels([], !filtersHaveTerms(filters)));
        setNextCursor(null);
      }
    }).finally(() => {
      if (generation !== requestGate.generation) return;
      requestGate.loading = false;
      setModelsLoading(false);
    });
  }

  function resetModels(): void {
    const filters = { query: "", tag: "" };
    setVersionModel(null);
    setModelSearch("");
    setModelTag("");
    setCommittedFilters(filters);
    setModels(mergeImageModels([]));
    setNextCursor(null);
    loadModels(undefined, filters, false, true);
  }

  useEffect(() => { resetModels(); }, []);

  function searchModels(): void {
    const filters = { query: modelSearch.trim(), tag: modelTag.trim() };
    setVersionModel(null);
    setCommittedFilters(filters);
    setModels(mergeImageModels([], !filtersHaveTerms(filters)));
    setNextCursor(null);
    loadModels(undefined, filters, false, true);
  }

  function clearTagFilter(): void {
    const filters = { query: modelSearch.trim(), tag: "" };
    setModelTag("");
    setVersionModel(null);
    setCommittedFilters(filters);
    setModels(mergeImageModels([], !filtersHaveTerms(filters)));
    setNextCursor(null);
    loadModels(undefined, filters, false, true);
  }

  function handleModelScroll(event: React.UIEvent<HTMLElement>): void {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (versionModel || modelsLoading || nextCursor === null || scrollHeight - scrollTop - clientHeight > 140) return;
    loadModels(nextCursor, committedFilters, true, false);
  }

  function selectModel(model: ImageModel): void {
    const versions = model.versions ?? [];
    if (versions.length > 1) {
      setVersionModel(model);
      return;
    }
    const ref = versions[0]?.ref ?? model.ref;
    setSelectedModel(selectionFromCatalog([model], ref) ?? model);
    onChange({ image_model: ref });
  }

  function modelPreview(model: ImageModel) {
    return model.imageUrl ? <SkeletonImage src={model.imageUrl} alt="" loading="lazy" decoding="async" fallbackLabel={`${model.name}预览暂不可用`} /> : <div className="model-preview-placeholder" aria-hidden="true">{model.name.slice(0, 1)}</div>;
  }

  return <section className="model-selection">
    <div className="model-selection-toolbar"><form className="model-search" onSubmit={(event) => { event.preventDefault(); searchModels(); }}><div className="model-filter-inputs"><input type="search" maxLength={80} value={modelSearch} onChange={(event) => setModelSearch(event.target.value)} placeholder="搜索 Checkpoint…" aria-label="搜索 Checkpoint" /><div className="model-tag-filter"><input type="text" maxLength={80} value={modelTag} onChange={(event) => setModelTag(event.target.value)} placeholder="按 tag 精确筛选…" aria-label="Checkpoint tag" />{modelTag ? <button type="button" aria-label="清空 tag 筛选" onClick={clearTagFilter}>×</button> : null}</div></div><button className="primary" type="submit">搜索</button></form>{versionModel ? <button className="secondary outlined" type="button" onClick={() => setVersionModel(null)}>← 返回模型</button> : <small>当前模型：{selected.name}</small>}</div>
    <div className="model-selection-scroll" onScroll={handleModelScroll}>{versionModel ? <div className="model-selection-grid">{versionModel.versions?.map((version) => <button className={`model-selection-card ${version.ref === values.image_model ? "selected" : ""}`} type="button" key={version.ref} aria-pressed={version.ref === values.image_model} onClick={() => { setSelectedModel(selectionFromCatalog([versionModel], version.ref) ?? versionModel); onChange({ image_model: version.ref }); setVersionModel(null); }}><span className="model-selection-cover">{modelPreview(version)}</span><span className="model-selection-title"><strong>{version.name}</strong></span></button>)}</div> : modelsLoading && models.length <= 1 ? <SkeletonGrid items={6} variant="persona" label="正在读取模型目录" /> : modelsError && models.length <= 1 ? <div className="model-state" role="alert"><p>{modelsError}</p><button className="secondary outlined" type="button" onClick={searchModels}>重试</button></div> : <><div className="model-selection-grid">{models.map((model) => <button className={`model-selection-card ${model.ref === values.image_model || model.versions?.some((version) => version.ref === values.image_model) ? "selected" : ""}`} type="button" key={model.ref} aria-pressed={model.ref === values.image_model} onClick={() => selectModel(model)}><span className="model-selection-cover">{modelPreview(model)}</span><span className="model-selection-title"><strong>{model.name}</strong>{model.versions && model.versions.length > 1 ? <small>{model.versions.length} 个版本</small> : null}</span></button>)}</div>{modelsLoading ? <SkeletonGrid items={3} variant="persona" label="正在加载更多模型" className="model-loading-more" /> : null}<div className="model-page-state">{modelsLoading ? null : modelsError ? <button className="secondary outlined" type="button" onClick={() => nextCursor !== null && loadModels(nextCursor, committedFilters, true, false)}>加载失败，重试</button> : models.length === 0 && nextCursor !== null ? <button className="secondary outlined" type="button" onClick={() => loadModels(nextCursor, committedFilters, true, false)}>加载更多匹配模型</button> : models.length === 0 ? "没有匹配的 Checkpoint" : nextCursor === null ? "已经到底了" : "继续向下滚动以加载更多"}</div></>}</div>
  </section>;
}

function ImageSettings({ values, onChange }: { values: EditableSettings; onChange: (patch: Partial<EditableSettings>) => void }) {
  return <div className="form-grid">
    <label>CFG<input type="number" min="1" max="30" step="0.5" value={values.image_cfg} onChange={(event) => onChange({ image_cfg: event.target.value })} /></label>
    <label>Sampler<select value={values.image_sampler} onChange={(event) => onChange({ image_sampler: event.target.value })}><option>Euler a</option><option>Euler</option><option>DPM++ 2M</option><option>DPM++ 2M SDE</option></select></label>
    <label>Scheduler<select value={values.image_scheduler} onChange={(event) => onChange({ image_scheduler: event.target.value })}><option value="normal">Normal</option><option value="karras">Karras</option><option value="exponential">Exponential</option></select></label>
    <label>分辨率<select value={values.image_resolution} onChange={(event) => onChange({ image_resolution: event.target.value })}>{DISCORD_IMAGE_PRESETS.map((preset) => <option value={preset.resolution} key={preset.resolution}>{preset.width} × {preset.height}（{preset.aspectRatio} · {preset.label}）</option>)}</select></label>
    <label>Steps<input value="20" readOnly aria-readonly="true" /></label>
  </div>;
}
